import React, { useEffect, useRef } from "react";
import "./lease-overlap-modal.css";
import { GetImages } from "../../utils/GetImages";
import DateHelper from "../../utils/DateHelper";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import PaymentIcon from "../../assets/Payment-Icon.svg";

interface LeaseOverlapModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  existingLease: {
    type: string;
    startDate: string;
    endDate: string;
    securityDeposit: string;
    securityDepositRaw: string;
    rent: string;
    rentRaw: string;
  };
  newLeaseEndDate: string;
  isLoading?: boolean;
}

const LeaseOverlapModal: React.FC<LeaseOverlapModalProps> = ({
  isOpen,
  onClose,
  onContinue,
  existingLease,
  newLeaseEndDate,
  isLoading = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle click outside to close (optional, can be removed if strictly modal)
  const handleOutsideClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      // Optional: onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="lease-overlap-modal-overlay" onClick={handleOutsideClick}>
      <div className="lease-overlap-modal-container" ref={modalRef}>
        {/* Header */}
        <div className="lease-overlap-modal-header">
          <h2 className="lease-overlap-modal-title">
            New Lease Dates Overlapped
          </h2>
          <button
            className="lease-overlap-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#64748B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="lease-overlap-modal-body">
          {/* Warning Message */}
          <div className="lease-overlap-warning-box">
            <p className="lease-overlap-warning-text">
              New lease dates overlap with the{" "}
              <strong>existing lease agreement</strong>. If you continue, the
              existing lease end date will be set to{" "}
              <strong>{newLeaseEndDate}</strong>. Do you wish to proceed?
            </p>
          </div>

          {/* Existing Lease Details Section */}
          <h3 className="lease-overlap-section-title">
            Existing Lease Details
          </h3>

          <div className="lease-summary-card lease-overlap-modal-lease-summary-card">
            {isLoading ? (
              <>
                {/* Skeleton Header */}
                <div className="lease-summary-card__header">
                  <div className="lease-summary-card__lease-type">
                    <div
                      className="lease-summary-card__label"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        className="skeleton-loader skeleton-badge"
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                        }}
                      ></div>
                      <div
                        className="skeleton-loader skeleton-text"
                        style={{ width: "80px" }}
                      ></div>
                    </div>
                    <div
                      className="skeleton-loader skeleton-badge"
                      style={{ width: "100px" }}
                    ></div>
                  </div>
                </div>

                {/* Skeleton Dates */}
                <div className="lease-summary-card__dates">
                  <div className="lease-summary-card__date-item">
                    <div
                      className="lease-summary-card__date-label"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "4px",
                      }}
                    >
                      <div
                        className="skeleton-loader skeleton-badge"
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                        }}
                      ></div>
                      <div
                        className="skeleton-loader skeleton-text"
                        style={{ width: "70px" }}
                      ></div>
                    </div>
                    <div
                      className="lease-summary-card__date-value skeleton-loader skeleton-text"
                      style={{ width: "100px", height: "20px" }}
                    ></div>
                  </div>
                  <div className="lease-summary-card__date-item">
                    <div
                      className="lease-summary-card__date-label"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "4px",
                      }}
                    >
                      <div
                        className="skeleton-loader skeleton-badge"
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                        }}
                      ></div>
                      <div
                        className="skeleton-loader skeleton-text"
                        style={{ width: "60px" }}
                      ></div>
                    </div>
                    <div
                      className="lease-summary-card__date-value skeleton-loader skeleton-text"
                      style={{ width: "100px", height: "20px" }}
                    ></div>
                  </div>
                </div>

                {/* Skeleton Amounts */}
                <div className="lease-summary-card__amounts">
                  <div className="lease-summary-card__amount-item">
                    <div
                      className="lease-summary-card__amount-label"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "4px",
                      }}
                    >
                      <div
                        className="skeleton-loader skeleton-badge"
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                        }}
                      ></div>
                      <div
                        className="skeleton-loader skeleton-text"
                        style={{ width: "100px" }}
                      ></div>
                    </div>
                    <div
                      className="lease-summary-card__amount-value skeleton-loader skeleton-text"
                      style={{ width: "80px", height: "24px" }}
                    ></div>
                  </div>
                  <div className="lease-summary-card__amount-item">
                    <div
                      className="lease-summary-card__amount-label"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "4px",
                      }}
                    >
                      <div
                        className="skeleton-loader skeleton-badge"
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                        }}
                      ></div>
                      <div
                        className="skeleton-loader skeleton-text"
                        style={{ width: "40px" }}
                      ></div>
                    </div>
                    <div
                      className="lease-summary-card__amount-value skeleton-loader skeleton-text"
                      style={{ width: "80px", height: "24px" }}
                    ></div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Header */}
                <div className="lease-summary-card__header">
                  <div className="lease-summary-card__lease-type">
                    <div className="lease-summary-card__label">
                      <img
                        src={GetImages.documentIcon}
                        alt="lease-icon"
                        width="20"
                        height="20"
                        className="stat-icon"
                      />
                      Lease Type:
                    </div>
                    <span className="lease-summary-card__badge">
                      {existingLease.type}
                    </span>
                  </div>
                </div>

                {/* Dates */}
                <div className="lease-summary-card__dates">
                  <div className="lease-summary-card__date-item">
                    <div className="lease-summary-card__date-label">
                      <img
                        src={GetImages.calendarIcon}
                        alt="lease-icon"
                        width="20"
                        height="20"
                        className="stat-icon"
                      />
                      Start Date
                    </div>
                    <div className="lease-summary-card__date-value">
                      {existingLease.startDate || "N/A"}
                    </div>
                  </div>
                  <div className="lease-summary-card__date-item">
                    <div className="lease-summary-card__date-label">
                      <img
                        src={GetImages.calendarIcon}
                        alt="lease-icon"
                        width="20"
                        height="20"
                        className="stat-icon"
                      />
                      End Date
                    </div>
                    <div className="lease-summary-card__date-value">
                      {existingLease.endDate || "N/A"}
                    </div>
                  </div>
                </div>

                {/* Amounts */}
                <div className="lease-summary-card__amounts">
                  <div className="lease-summary-card__amount-item">
                    <div className="lease-summary-card__amount-label">
                      <img
                        src={GetImages.secureIcon}
                        alt="lease-icon"
                        width="20"
                        height="20"
                        className="stat-icon"
                        style={{ filter: "var(--icon-black-white)" }}
                      />
                      Security Deposit
                    </div>
                    <div className="lease-summary-card__amount-value">
                      <CurrencyTooltip
                        content={existingLease.securityDepositRaw}
                        formatted={existingLease.securityDeposit}
                      >
                        <span>{existingLease.securityDeposit
                        ? existingLease.securityDeposit
                        : "N/A"}</span>
                      </CurrencyTooltip>
                    </div>
                  </div>
                  <div className="lease-summary-card__amount-item">
                    <div className="lease-summary-card__amount-label">
                      <img
                        src={PaymentIcon}
                        alt="lease-icon"
                        width="20"
                        height="20"
                        className="stat-icon"
                        style={{ filter: "var(--icon-black-white)" }}
                      />
                      Rent
                    </div>
                    <div className="lease-summary-card__amount-value">
                      <CurrencyTooltip
                        content={existingLease.rentRaw}
                        formatted={existingLease.rent}
                      >
                        <span>{existingLease.rent
                        ? existingLease.rent
                        : "N/A"}</span>
                      </CurrencyTooltip>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="lease-overlap-modal-footer">
          <button
            className="lease-overlap-btn lease-overlap-btn-secondary"
            style={{
              border: "none",
              background: "transparent",
              color: "var(--Primary-Regular)",
              fontWeight: 600,
            }}
            onClick={onContinue}
          >
            Continue
          </button>
          <button
            className="lease-overlap-btn lease-overlap-btn-primary"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaseOverlapModal;
